// OP-3617 — Formatação de texto compartilhada pelo módulo Estoque: plural e o
// resumo por regime (peça × consumível), que nunca se somam num número só.

export const faltaMotivo = (motivo: string, texto: string): string | null => (motivo.trim() ? null : texto)

export const plural = (n: number, singular: string, pluralForm: string): string =>
  `${n} ${n === 1 ? singular : pluralForm}`

export interface QuantidadesPorRegime {
  pecas: number
  consumo: number
}

/** "2 peças · 5 un." — só os regimes com quantidade; os dois zerados viram "0". */
export function textoRegime(q: QuantidadesPorRegime): string {
  const partes: string[] = []
  const pecas = Number(q.pecas) || 0
  const consumo = Number(q.consumo) || 0
  if (pecas > 0) partes.push(plural(pecas, 'peça', 'peças'))
  if (consumo > 0) partes.push(`${consumo} un.`)
  return partes.length ? partes.join(' · ') : '0'
}

export interface ProgressoPorRegime {
  pecasFeitas: number
  pecasPedidas: number
  consumoFeito: number
  consumoPedido: number
}

/**
 * "1/2 peças · 3/10 un." — o progresso de cada regime contra o pedido.
 * Regime sem nada pedido não aparece; pedido sem item nenhum vira "—".
 */
export function progressoRegime(p: ProgressoPorRegime): string {
  const partes: string[] = []
  const pecasPedidas = Number(p.pecasPedidas) || 0
  const consumoPedido = Number(p.consumoPedido) || 0
  if (pecasPedidas > 0) partes.push(`${Number(p.pecasFeitas) || 0}/${pecasPedidas} peças`)
  if (consumoPedido > 0) partes.push(`${Number(p.consumoFeito) || 0}/${consumoPedido} un.`)
  return partes.length ? partes.join(' · ') : '—'
}

/**
 * OP-3619 — o telefone do contato como número discável.
 *
 * O campo é texto livre: chega "(11) 99999-0000", "+55 11 99999-0000" ou com
 * o DDI colado. Devolve só dígitos com DDI uma vez só — antepor 55 a quem já
 * o tem produz um número inexistente no link do WhatsApp.
 */
export function numeroComDdi(bruto: string | null | undefined): string | null {
  const digitos = String(bruto ?? '').replace(/\D/g, '')
  if (digitos.length < 10) return null
  if (digitos.length === 10 || digitos.length === 11) return `55${digitos}`
  if (digitos.length <= 13 && digitos.indexOf('55') === 0) return digitos
  return digitos
}

export type SituacaoPrazo = 'sem-prazo' | 'vencido' | 'hoje' | 'futuro'

/** Hoje em ISO local — comparar data com `new Date()` erraria por fuso. */
export function hojeIso(agora: Date = new Date()): string {
  const mes = String(agora.getMonth() + 1).padStart(2, '0')
  const dia = String(agora.getDate()).padStart(2, '0')
  return `${agora.getFullYear()}-${mes}-${dia}`
}

/**
 * OP-3619 — em que pé está o prazo.
 *
 * Na lista do celular não cabe a data e mais nada: o que decide a ordem do
 * dia é se já venceu, se vence hoje ou se ainda dá tempo.
 */
export function situacaoPrazo(prazo: string | null | undefined, hoje?: string): SituacaoPrazo {
  if (!prazo) return 'sem-prazo'
  const dia = String(prazo).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return 'sem-prazo'
  const ref = hoje || hojeIso()
  if (dia < ref) return 'vencido'
  if (dia === ref) return 'hoje'
  return 'futuro'
}

export interface PrazoUi {
  texto: string
  alerta: boolean
}

/**
 * A data como a lista mostra: texto curto e se merece destaque. O rótulo
 * muda com o que está vencendo — prazo do pedido, retorno da remessa.
 */
export function prazoUi(prazo: string | null | undefined, hoje?: string, rotulo = 'Prazo'): PrazoUi {
  const situacao = situacaoPrazo(prazo, hoje)
  if (situacao === 'sem-prazo') return { texto: `Sem ${rotulo.toLowerCase()}`, alerta: false }
  const dia = String(prazo).slice(0, 10)
  const data = `${dia.slice(8, 10)}/${dia.slice(5, 7)}`
  if (situacao === 'vencido') return { texto: `${rotulo} vencido em ${data}`, alerta: true }
  if (situacao === 'hoje') return { texto: `${rotulo} hoje`, alerta: true }
  return { texto: `${rotulo} ${data}`, alerta: false }
}

export const paraBusca = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
